/**
 * @documental
 * @export TabbedContainer
 * @import
 * import { TabbedContainer } from "capitalrx-components-2.0/lib/general";
 */
import { FC, ReactNode, useCallback, useState } from "react";

import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { BaseProps } from "../../BaseProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { withSubComponents } from "../../utilities/withSubComponents";
import { SelectedTabChangeHandler, TabSet } from "../TabSet";
import { TooltipProps } from "../Tooltip";
import { TabPane } from "./TabPane";
import { getTabPanes } from "./helpers";
import { createComponentStyleHook } from "../../tokens";

import legacyStyles from "./legacy/styles.module.scss";
import tokenStyles from "./styles.module.css";

/**
 * The tab view component properties.
 */
export interface TabbedContainerProps extends BaseProps {
  /**
   * The tab panes to display.
   */
  readonly children?: ReactNode;

  /**
   * Indicates whether tabs should span evenly across the entire tabs component area. If not set, defaults to false.
   */
  readonly spanTabsAcross?: boolean;

  /**
   * The ID of the currently selected tab pane. If not specfied the ID of the first tab pane is used. If `null`
   * is specified then no tabs are selected.
   */
  readonly selectedTab?: string | null;

  /**
   * Indicates whether the tabbed container is disabled.
   */
  readonly disabled?: boolean;

  /**
   * The tooltip appearance. If not specified defaults to "dark".
   */
  readonly tooltipAppearance?: TooltipProps["appearance"];

  /**
   * The tooltip alignment. If not specifies defaults to "cursor".
   */
  readonly tooltipAlignment?: TooltipProps["alignment"];

  /**
   * Called when the selected tab is changed.
   * <br>
   * <br>
   * **NOTE:** When using asynchronous tabs, do not use this function to update other UI elements in response to
   * the tab change as this may lead to unexpected behaviour. Instead, you should use the
   * `TabbedContainer.TabPane.onSelected` or `TabbedContainer.onSelectedTabChangeComplete` events.
   */
  readonly onSelectedTabChange?: SelectedTabChangeHandler;

  /**
   * Called when the selected tab has been successfully changed. Unlike `onSelectedTabChange` this event fires
   * only once the `TabbedContainer` has succesfully switched to the requested tab.
   */
  readonly onSelectedTabChangeComplete?: SelectedTabChangeHandler;
}

/**
 * The tab view component subcomponents.
 */
export interface TabbedContainerSubComponents {
  /**
   * Represents a single tab pane which can be placed within the TabView component.
   */
  readonly TabPane: typeof TabPane;
}

const useStyles = createComponentStyleHook<TabbedContainerProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "tabbed_container",
});

/**
 * Displays tabs alongside their respective content.
 */
export const TabbedContainer = withSubComponents<FC<TabbedContainerProps>, TabbedContainerSubComponents>(
  {
    TabPane,
  },
  (props) => {
    const {
      selectedTab,
      children,
      onSelectedTabChange,
      onSelectedTabChangeComplete,
      tooltipAppearance,
      tooltipAlignment,
      spanTabsAcross,
      disabled = false,
    } = props;
    const { mode, styles, StyleModeBoundary } = useStyles(props);
    const [selectedPaneId, setSelectedPaneId] = useState<string | null>();

    const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
      className: [
        styles.TabbedContainer,
        "tabbed-container",
        {
          disabled: disabled,
        },
      ],
      testIdPrefix: "tabbed-container",
    });

    const tabPanes = getTabPanes(children);
    const paneIds = tabPanes.map((tabPane) => tabPane.id).join(",");

    const tabSelectHandler = useCallback(
      (tabId: string): void => {
        const selectedPane = tabPanes.find((tabPane) => tabPane.id === tabId);
        if (!selectedPane) setSelectedPaneId(null);
        else setSelectedPaneId(tabId);

        selectedPane?.onSelect?.(tabId);
      },
      [paneIds],
    );

    return (
      <StyleModeBoundary mode={mode}>
        <div {...baseHtmlProps}>
          <TabSet
            disabled={disabled}
            spanTabsAcross={spanTabsAcross}
            selectedTab={selectedTab}
            tooltipAlignment={tooltipAlignment}
            tooltipAppearance={tooltipAppearance}
            onSelectedTabChange={onSelectedTabChange}
            onSelectedTabChangeComplete={onSelectedTabChangeComplete}
          >
            {tabPanes?.map((props) => {
              const { id, label } = props;

              return (
                <TabSet.Tab key={id} {...props} onSelect={tabSelectHandler}>
                  {label}
                </TabSet.Tab>
              );
            })}
          </TabSet>
          <div className={classNames(styles.TabPanel, "tab-panel")}>
            {tabPanes?.map((props) => {
              const { id, keepHiddenContentInDOM = false, children } = props;
              const selected = id === selectedPaneId;

              if (!selected && !keepHiddenContentInDOM) return null;

              return (
                <div
                  key={id}
                  className={classNames("tab-panel-content", { hidden: !selected })}
                  hidden={!selected}
                  role="tabpanel"
                  id={`tabpanel-${id}`}
                  aria-labelledby={`tab-${id}`}
                >
                  <Body size="inherit">{children}</Body>
                </div>
              );
            })}
          </div>
        </div>
      </StyleModeBoundary>
    );
  },
);
